import React from 'react'
import { Box, Card, Container, Element, Heading, Level, Section } from 'react-bulma-components'
import Layout from '../../components/layout/layout'
import useUrlId from '../../hooks/use-url-id'
import { useWorkerQuery } from '../../queries/users'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import PageTitle from '../../components/pages/page-title'
import useStore from '../../store'
import { getWorkerEmployerRelationData } from '../../utils/users'
import { useIsInternalWorker } from '../../hooks/use-is-internal-worker'
import { Organization } from '../../api/organizations'
import { useWorkPeriodsQuery } from '../../queries/work-periods'
import Protected from '../../components/protected/protected'
import WorkPeriodsList from '../../components/work-periods/list'
import { addDays, endOfDay, parseISO, startOfDay } from 'date-fns'
import Avatar from '../../components/avatar/avatar'
import WorkerActions from '../../components/workers/worker-actions'
import useFilters from '../../hooks/filters'
import { FiltersResourcesEnum } from '../../store/filters'
import { useNavigate } from 'react-router-dom'
import useWorkersSearch from '../../hooks/use-workers-search'

/**
 * Page de détails d'un travailleur
 * Affiche les informations personnelles, associations, historique etc.
 */
const WorkerDetails: React.FC = () => {
  const currentOrganization = useStore(state => state.session.currentOrganization as Organization)
  useWorkersSearch('') // TEMP: Preload workers search to avoid empty workers filter on work periods list
  const { setFilters, filters } = useFilters(FiltersResourcesEnum.workPeriods)
  const navigate = useNavigate()
  const { id } = useUrlId()
  const { data: worker } = useWorkerQuery(id as string)

  const { data: workPeriods } = useWorkPeriodsQuery(
    {
      user: [id],
      start: startOfDay(parseISO(addDays(new Date(), -7).toISOString())),
      end: endOfDay(parseISO(addDays(new Date(), 7).toISOString())),
      summary: true,
    },
    { enabled: Boolean(id) },
  )

  const { isInternalWorker, association } = useIsInternalWorker(worker)
  const isExternalWorker = !isInternalWorker
  const employerRelation = worker && getWorkerEmployerRelationData(worker, currentOrganization)

  const breadcrumbs = useBreadcrumbs(id)

  const isEmployerCollaborationInactive = Boolean(employerRelation?.collaboration?.isInactive)

  const handleWorkPeriodsListNavigation = () => {
    if (!worker) return
    setFilters(FiltersResourcesEnum.workPeriods, {
      ...filters,
      user: [worker._id],
    })
    navigate('/work-periods')
  }

  if (!worker) return null

  return (
    <Layout>
      <Section>
        <Container max={true}>
          {/* En-tête */}
          <Level>
            <Level.Side>
              <Breadcrumbs items={breadcrumbs} />
            </Level.Side>
          </Level>

          {/* Titre et statut */}
          <Level alignItems="flex-start" mb={6}>
            <Level.Side>
              <PageTitle title={<Avatar user={worker} />} subTitle={association?.internalRef} />
            </Level.Side>
          </Level>

          <Heading size={5}>Journées de travail des 7 derniers et prochains jours</Heading>
          <Box mb={1}>
            <WorkPeriodsList
              workPeriods={workPeriods ?? []}
              omitKeys={['validateAction', 'user', 'mission.dates']}
            />
          </Box>
          <Protected roles={['employerMember']}>
            <Element textAlign={'right'} textColor="link">
              <Element renderAs="a" onClick={() => handleWorkPeriodsListNavigation()}>
                Voir plus
              </Element>
            </Element>
          </Protected>
          <Protected
            roles={[
              { role: 'employerMember', action: { resource: 'users', name: 'update' } },
              'interimAgencyMember',
            ]}
          >
            <Heading size={5} mt={6}>
              Informations & Actions
            </Heading>
            <Card>
              <Card.Content>
                <WorkerActions
                  worker={worker}
                  isEmployerCollaborationInactive={isEmployerCollaborationInactive}
                  isExternalWorker={isExternalWorker}
                  association={association}
                />
              </Card.Content>
            </Card>
          </Protected>
        </Container>
      </Section>
    </Layout>
  )
}

export default WorkerDetails

// Hooks
const useBreadcrumbs = (workerId?: string) =>
  React.useMemo(
    () => [
      {
        label: 'Travailleurs',
        to: '/settings/workers',
      },
      {
        label: 'Détails du travailleur',
      },
    ],
    [workerId],
  )
